
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { mixins } from 'vue-class-component';
import Notification from '@/mixins/Notification';
import { Venue } from '@/interfaces/models/Venue';
import { Sentry } from '@/plugins/sentry';
import moment from 'moment';
import { InputType } from '@/enums/InputType';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';

const venue = namespace('venue');

interface FeedbackFormModel {
  name: string;
  email: string;
  comments: string;
}

@Component({
  components: { VFormBuilder },
})
export default class FeedbackDialog extends mixins(Notification) {
  @venue.State('active') public venue!: Venue;

  public $refs!: {
    form: InstanceType<typeof VFormBuilder> & { form: FeedbackFormModel; validate: () => Promise<boolean> };
  };

  public visibility: boolean = false;
  public submitting: boolean = false;

  get items() {
    return [
      {
        name: 'name',
        type: InputType.Text,
        label: this.$t('feedback.fields.name').toString(),
        autocomplete: 'name',
      },
      {
        name: 'email',
        type: InputType.Text,
        label: this.$t('feedback.fields.email').toString(),
        autocomplete: 'email',
        rules: 'required|email',
      },
      {
        name: 'comments',
        type: InputType.Textarea,
        label: this.$t('feedback.fields.comments').toString(),
        rules: 'required|min:10|max:1000',
      },
    ];
  }

  get hasSentryFeedback() {
    return !!process.env.VUE_APP_SENTRY_ENVIRONMENT;
  }

  public openDialog() {
    if (!this.hasSentryFeedback) {
      return;
    }

    this.visibility = true;
  }

  public async submitFeedback() {
    if (this.submitting || !this.hasSentryFeedback) {
      return;
    }

    const isValid = await this.$refs.form?.validate();

    if (!isValid) {
      return;
    }

    this.submitting = true;

    const formValues = this.$refs.form.form;
    const reporterName = formValues.name?.trim() || null;
    const reporterEmail = formValues.email?.trim().toLowerCase() || null;
    const reporterEmailDomain = reporterEmail?.includes('@') ? reporterEmail.split('@')[1] : null;

    const normalizedName = (reporterName || 'anonymous').replace(/\s+/g, '-').toLowerCase();
    const utcNow = moment.utc();
    const datePart = utcNow.format('DD.MM.YYYY');
    const timePart = utcNow.valueOf();
    const feedbackId = `feedback-${normalizedName}-${datePart}:${timePart}`;

    Sentry.withScope((scope) => {
      scope.setTag('source', 'user-feedback');
      scope.setTag('feedback_channel', 'app-button');
      scope.setTag('feedback_id', feedbackId);
      scope.setTag('route_name', `${this.$route?.name || 'unknown'}`);
      scope.setUser({
        email: reporterEmail,
        username: reporterName,
      });
      scope.setFingerprint(['user-feedback', feedbackId]);
      scope.setContext('feedback', {
        id: feedbackId,
        name: reporterName,
        email: reporterEmail,
        comments: formValues.comments?.trim(),
        path: this.$route?.fullPath || '',
        venueId: this.venue?._id || null,
        venueName: this.venue?.name || null,
      });

      if (reporterEmailDomain) {
        scope.setTag('reporter_email_domain', reporterEmailDomain);
      }

      if (reporterName) {
        scope.setTag('reporter_name', reporterName);
      }

      Sentry.captureMessage('User feedback submitted');
    });

    this.submitting = false;
    this.visibility = false;

    this.notifySuccess(this.$t('feedback.notification.sent').toString());
  }
}
